import { ProductVariant } from '@getstep/proto/common'
import { AuthenticationFailureError } from '@getstep/sdk/dist/http/auth/AuthenticationFailureError'
import { notNullish, nullish } from '@getstep/sdk/dist/util/Assert'
import { observer } from 'mobx-react-lite'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import type { FunctionComponent, ReactNode } from 'react'
import { useContext, useEffect, useState } from 'react'

import styles from './playbook-page.module.scss'
import FullScreenLoading from '@components/playbook/full-screen-loading'
import Header from '@components/playbook/header'
import { PreHeader } from '@components/playbook/pre-header'
import { LoggedOutURLS, OnboardingURLS, URLS } from '@components/playbook/urls'
import { DEFAULT_OG_IMAGE } from '@contentful-components/page-meta/default-meta'
import analyticsService from '@lib/analytics/AnalyticsService'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { usePlaybook } from '@lib/hooks/usePlaybook'
import { PlaybookContext } from '@lib/store/playbook/context'
import { SessionStore } from '@lib/store/session'
import { SessionContext } from '@lib/store/session/context'
import { useStores } from '@lib/store/useStores'
import { playbookUserHasNotKYCed } from '@lib/utils/kyc'
import { isPrd } from '@shared/util/env'

export const getPlaybookLayout = () => {
    return (children: ReactNode) => {
        return <PlaybookWrapper>{children}</PlaybookWrapper>
    }
}

export const PlaybookWrapper: FunctionComponent<Props> = observer(
    ({ children }) => {
        const { sdk, linkingStore, featureOverrideStore } = useStores()
        const sessionStore = useContext(SessionContext)
        const playbook = usePlaybook()
        const router = useRouter()

        const [session] = useState(
            sessionStore ??
                new SessionStore(sdk, linkingStore, featureOverrideStore),
        )
        const { authorizedStore } = session
        const userId = authorizedStore?.userStore.user.id

        const pathName = usePathname()
        const searchParams = useSearchParams()
        const [isLayoutLoading, setIsLayoutLoading] = useState(true)

        useEffect(() => {
            localStorage.setItem('isOnboarding', 'lending')
        }, [])

        useEffectAsync(async () => {
            if (!searchParams) return

            // Set playbook-specific user attributes
            await analyticsService.setUserAttributes({
                productVariant: ProductVariant.META_LENDING,
            })
        }, [searchParams])

        // Handle authentication failures gracefully in playbook
        useEffect(() => {
            const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
                if (event.reason instanceof AuthenticationFailureError) {
                    if (!isPrd()) {
                        console.debug(
                            'Unhandled authentication failure, redirecting to login',
                        )
                    }
                    event.preventDefault() // Prevent the error from appearing in console
                    router.replace(URLS.EXCLUSIVE_OFFER)
                }
            }

            window.addEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            )
            return () => {
                window.removeEventListener(
                    'unhandledrejection',
                    handleUnhandledRejection,
                )
            }
        }, [router])

        useEffectAsync(async () => {
            if (!session.loaded) {
                playbook.setIsLoading(true)
                try {
                    await session.restore()
                    if (nullish(session.authorizedStore)) {
                        if (!isPrd()) {
                            console.debug('user is logged out')
                        }
                        if (
                            nullish(
                                LoggedOutURLS.find((url) => url === pathName),
                            )
                        ) {
                            router.replace(URLS.EXCLUSIVE_OFFER)
                        }
                    } else if (
                        playbookUserHasNotKYCed(session.authorizedStore)
                    ) {
                        if (!isPrd()) {
                            console.debug('user is not onboarded')
                        }
                        router.replace(URLS.EXCLUSIVE_OFFER)
                    } else if (
                        notNullish(
                            OnboardingURLS.find((url) => url === pathName),
                        )
                    ) {
                        if (!isPrd()) {
                            console.debug(
                                'user should not be on pre onboarded screens after onboarding',
                            )
                        }
                        router.replace(URLS.ACCOUNT)
                    }
                } catch (error) {
                    if (error instanceof AuthenticationFailureError) {
                        if (!isPrd()) {
                            console.debug(
                                'Authentication failed, redirecting to login',
                            )
                        }
                        router.replace(URLS.EXCLUSIVE_OFFER)
                    } else {
                        console.error(
                            'Unexpected error during session restore:',
                            error,
                        )
                        router.replace(URLS.EXCLUSIVE_OFFER)
                    }
                } finally {
                    playbook.setIsLoading(false)
                    setIsLayoutLoading(false)
                }
            } else {
                if (!isPrd()) {
                    console.debug('session already loaded')
                }
            }
        }, [userId])

        if (isLayoutLoading) {
            return (
                <SessionContext.Provider value={session}>
                    <PlaybookContext.Provider value={playbook}>
                        <NextSeo
                            title='Step EarlyPay'
                            description='Borrow up to $250 instantly with Step EarlyPay'
                            openGraph={{
                                title: 'Step EarlyPay',
                                description:
                                    'Borrow up to $250 instantly with Step EarlyPay',
                                images: [
                                    {
                                        url: DEFAULT_OG_IMAGE,
                                        width: 1200,
                                        height: 630,
                                        alt: 'Borrow up to $250 instantly with Step EarlyPay',
                                    },
                                ],
                            }}
                            noindex
                            nofollow
                        />
                        <div className={styles.wrapper}>
                            <PreHeader />
                            <div className={styles.content}>
                                <Header />
                                <FullScreenLoading />
                            </div>
                        </div>
                    </PlaybookContext.Provider>
                </SessionContext.Provider>
            )
        }

        return (
            <SessionContext.Provider value={session}>
                <PlaybookContext.Provider value={playbook}>
                    <NextSeo
                        title='Step EarlyPay | Step'
                        description='Borrow up to $250 instantly with Step EarlyPay'
                        openGraph={{
                            title: 'Step EarlyPay | Step',
                            description:
                                'Borrow up to $250 instantly with Step EarlyPay',
                        }}
                        noindex
                        nofollow
                    />
                    <div className={styles.wrapper}>
                        <PreHeader />
                        <div className={styles.content}>
                            <Header />
                            {children}
                        </div>
                    </div>
                </PlaybookContext.Provider>
            </SessionContext.Provider>
        )
    },
)

type Props = {
    children: ReactNode
}
